import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Branch } from '../../../interfaces/entities/branch';
import { uploadImage } from '../../../services/imageUploaderService';
import { defaultBranch } from '../../../defaultValues/defaultBranch';
import { BranchForm } from './branchForm';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetCompaniesQuery } from '../../../api/companyApi';
import { useToast } from '../../../hooks/useToast';
import { useCreateBranchMutation, useUpdateBranchMutation, useLazyCheckBranchUrlKeyAvailableQuery } from '../../../api/branchApi';
import { useAuthenticatedUser } from '../../../hooks/useAuthenticatedUser';
import { useLoader } from '../../../hooks/useLoader';
import { LoadingIndicator } from '../../shared/alerts/loadingIndicator';
import { getBranchImagePath } from '../../../services/storageService';

export const BranchAddContainer: React.FC = () => {
    const { currentUserIsOwnerOf, user } = useAuthenticatedUser();
    const { companyId } = useParams<{ companyId?: string }>(); // Mark id as optional
    const [branchImage, setBranchImage] = useState<File | null>(null);

    const navigate = useNavigate();

    const { data: companies = [], isLoading: loadingCompanies } = useGetCompaniesQuery();
    const [createBranch, createProcess] = useCreateBranchMutation();
    const [updateBranch, updateProcess] = useUpdateBranchMutation();
    const [checkUrlKey] = useLazyCheckBranchUrlKeyAvailableQuery();

    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    // Handle form submission
    const handleFormSubmit = async (values: Branch) => {
        try {
            showLoader();

            const { data: isAvailable } = await checkUrlKey({ urlKey: values.urlKey ?? '' });
            if (!isAvailable) {
                showError("URL Key is already in use. Please choose a different one.");
                return;
            }

            // Step 1: Prepare branch data
            const branchData: Branch = { ...values };

            // Step 2: Save branch data in Firestore and get the document ID
            const createdBranch = await createBranch(branchData).unwrap();

            if (createdBranch) {
                if (branchImage) {
                    const imagePath = getBranchImagePath(createdBranch.companyId, createdBranch.id);
                    const imageUrl = await uploadImage(imagePath, branchImage);
                    await updateBranch({ id: createdBranch.id, data: { ...createdBranch, image: imageUrl } }).unwrap();
                }
                showSuccess("Branch added successfully!");
                navigate(-1); // Navigate back to previous screen
            } else {
                showError("Failed to add branch!");
            }
        } catch (error: any) {
            console.error('Error while submitting form:', error);
            showError(`${error.message}`);
        } finally {
            hideLoader();
        }
    };

    const handleImageChange = (file: File | null) => {
        setBranchImage(file);
    };

    const handleCheckUrlKey = async (urlKey: string): Promise<boolean> => {
        const { data } = await checkUrlKey({ urlKey });
        return data ?? false;
    };

    const companyOptions = companies.map((company) => ({
        id: company.id,
        label: company.name,
    }));

    const isLoading = createProcess.isLoading || updateProcess.isLoading || loadingCompanies;

    if (isLoading) return <LoadingIndicator message='Loading branch data...' />;
    if (!user) return <div>Unauthorized</div>;
    if (!currentUserIsOwnerOf(companyId)) return <div>Unauthorized</div>;

    const initialValues: Branch = { ...defaultBranch(user.id), companyId: companyId ?? "" };

    return (
        <Box>
            <BranchForm
                title='Add New Branch'
                initialValues={initialValues}
                onSubmit={handleFormSubmit}
                companyOptions={companyOptions}
                onImageChange={handleImageChange}
                onCheckUrlKey={handleCheckUrlKey}
                isSubmitting={isLoading}
                onCancel={() => navigate(-1)}
            />
        </Box>
    );
};
